import React, { useState } from 'react';
import { Dialog } from '@headlessui/react';
import { HiXMark } from 'react-icons/hi2';
import { toast } from 'react-hot-toast';

const ClaimListingModal = ({ isOpen, onClose, onSubmit, organization }) => {
  const [formData, setFormData] = useState({
    documentType: 'sra_certificate',
    documentFiles: [],
    additionalInfo: '',
    termsAccepted: false
  });
  
  const [submitting, setSubmitting] = useState(false);
  
  const handleChange = (e) => {
    const { name, value, type, checked, files } = e.target;
    
    if (type === 'file') {
      setFormData({
        ...formData,
        documentFiles: Array.from(files)
      });
    } else if (type === 'checkbox') {
      setFormData({
        ...formData,
        [name]: checked
      });
    } else {
      setFormData({
        ...formData,
        [name]: value
      });
    }
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.termsAccepted) {
      toast.error('You must accept the terms and conditions');
      return;
    }
    
    if (formData.documentFiles.length === 0) {
      toast.error('Please upload at least one verification document');
      return;
    }
    
    setSubmitting(true);
    
    try {
      // For now, we'll just pass the form data to the parent component
      // In production, you'd handle file uploads here
      await onSubmit(formData);
      onClose();
    } catch (error) {
      console.error('Error submitting claim:', error);
      toast.error(error.response?.data?.error || 'Failed to submit claim');
    } finally {
      setSubmitting(false);
    }
  };
  
  return (
    <Dialog open={isOpen} onClose={() => !submitting && onClose()} className="relative z-50">
      <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
      
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <Dialog.Panel className="mx-auto max-w-xl rounded-lg bg-white shadow-xl">
          <div className="flex items-center justify-between p-6 border-b">
            <Dialog.Title className="text-xl font-semibold text-gray-900">
              Claim Your Listing
            </Dialog.Title>
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="text-gray-500 hover:text-gray-700 focus:outline-none"
            >
              <HiXMark className="h-5 w-5" />
            </button>
          </div>
          
          <div className="p-6 max-h-[70vh] overflow-y-auto">
            <div className="mb-6">
              <p className="text-sm text-gray-600 mb-2">
                You are claiming the listing for: <span className="font-medium text-gray-900">{organization?.practice_name || organization?.name}</span>
              </p>
              <p className="text-sm text-gray-600">
                To verify your identity and association with this organisation, please provide supporting documentation.
              </p>
            </div>
            
            <form onSubmit={handleSubmit}>
              <div className="mb-4">
                <label htmlFor="documentType" className="block text-sm font-medium text-gray-700 mb-1">
                  Document Type
                </label>
                <select
                  id="documentType"
                  name="documentType"
                  value={formData.documentType}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
                  required
                >
                  <option value="sra_certificate">SRA Certificate</option>
                  <option value="business_card">Business Card</option>
                  <option value="company_email">Company Email Verification</option>
                  <option value="other">Other Document</option>
                </select>
              </div>
              
              <div className="mb-4">
                <label htmlFor="documentFiles" className="block text-sm font-medium text-gray-700 mb-1">
                  Upload Verification Documents
                </label>
                <div className="border border-gray-300 border-dashed rounded-md p-6 text-center">
                  <input
                    id="documentFiles"
                    name="documentFiles"
                    type="file"
                    multiple
                    onChange={handleChange}
                    className="sr-only"
                  />
                  <label
                    htmlFor="documentFiles"
                    className="cursor-pointer block text-sm text-gray-600"
                  >
                    <div className="text-pink-600 hover:text-pink-500 mb-2">
                      <svg
                        className="h-8 w-8 mx-auto"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                        ></path>
                      </svg>
                    </div>
                    <p className="mb-1">Drag and drop files here, or click to select files</p>
                    <p className="text-xs">PDF, JPG, PNG up to 5MB each</p>
                  </label>
                  
                  {formData.documentFiles.length > 0 && (
                    <div className="mt-4 text-left">
                      <h4 className="text-xs font-medium text-gray-700 mb-2">Selected Files:</h4>
                      <ul className="space-y-1">
                        {formData.documentFiles.map((file, index) => (
                          <li key={index} className="text-xs text-gray-600 flex items-center">
                            <svg
                              className="h-4 w-4 text-pink-500 mr-2"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                              ></path>
                            </svg>
                            {file.name} ({Math.round(file.size / 1024)} KB)
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
              
              <div className="mb-6">
                <label htmlFor="additionalInfo" className="block text-sm font-medium text-gray-700 mb-1">
                  Additional Information (optional)
                </label>
                <textarea
                  id="additionalInfo"
                  name="additionalInfo"
                  value={formData.additionalInfo}
                  onChange={handleChange}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
                  placeholder="Provide any additional information to help verify your association with this organisation"
                />
              </div>
              
              <div className="mb-6">
                <div className="flex items-start">
                  <div className="flex items-center h-5">
                    <input
                      id="termsAccepted"
                      name="termsAccepted"
                      type="checkbox"
                      checked={formData.termsAccepted}
                      onChange={handleChange}
                      className="h-4 w-4 text-pink-600 focus:ring-pink-500 border-gray-300 rounded"
                      required
                    />
                  </div>
                  <div className="ml-3 text-sm">
                    <label htmlFor="termsAccepted" className="font-medium text-gray-700">
                      Terms and Conditions
                    </label>
                    <p className="text-gray-500">
                      I confirm that I am authorised to represent this organisation and that the information provided is accurate. I understand that false claims may result in account suspension.
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={onClose}
                  disabled={submitting}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-pink-600 to-pink-700 border border-transparent rounded-md shadow-sm hover:from-pink-700 hover:to-pink-800 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
                >
                  {submitting ? 'Submitting...' : 'Submit Claim'}
                </button>
              </div>
            </form>
          </div>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
};

export default ClaimListingModal; 